/**
 * 认证模块
 * Auth Module - auth.js
 * 封装 Supabase Auth 的注册、登录、登出功能
 */

import { initDb, isAvailable, getDb } from './db.js'

// 当前用户状态
let currentUser = null

/**
 * 检查登录状态
 * @returns {Promise<boolean>} 是否已登录
 */
export async function checkAuth() {
  if (!isAvailable()) {
    console.warn('[Auth] 数据库不可用，跳过登录检查')
    currentUser = { email: 'local-user', isLocal: true }
    return true
  }

  try {
    const db = getDb()
    const { data: { session } } = await db.auth.getSession()

    if (session) {
      currentUser = session.user
      console.log('[Auth] 用户已登录:', currentUser.email)
      return true
    }

    currentUser = null
    console.log('[Auth] 用户未登录')
    return false
  } catch (err) {
    console.error('[Auth] 检查登录状态失败:', err)
    return false
  }
}

/**
 * 注册新用户
 * @param {string} email - 邮箱
 * @param {string} password - 密码
 * @returns {Promise<{success: boolean, error: string|null}>}
 */
export async function signUp(email, password) {
  if (!isAvailable()) {
    // 降级模式：直接允许本地使用
    currentUser = { email, isLocal: true }
    return { success: true, error: null }
  }

  try {
    const db = getDb()
    const { data, error } = await db.auth.signUp({
      email,
      password,
    })

    if (error) {
      console.error('[Auth] 注册失败:', error.message)
      return { success: false, error: error.message }
    }

    currentUser = data.user
    console.log('[Auth] 注册成功:', currentUser.email)
    return { success: true, error: null }
  } catch (err) {
    console.error('[Auth] 注册异常:', err)
    return { success: false, error: '注册失败，请稍后重试' }
  }
}

/**
 * 用户登录
 * @param {string} email - 邮箱
 * @param {string} password - 密码
 * @returns {Promise<{success: boolean, error: string|null}>}
 */
export async function signIn(email, password) {
  if (!isAvailable()) {
    // 降级模式
    currentUser = { email, isLocal: true }
    return { success: true, error: null }
  }

  try {
    const db = getDb()
    const { data, error } = await db.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      console.error('[Auth] 登录失败:', error.message)
      return { success: false, error: error.message }
    }

    currentUser = data.user
    console.log('[Auth] 登录成功:', currentUser.email)
    return { success: true, error: null }
  } catch (err) {
    console.error('[Auth] 登录异常:', err)
    return { success: false, error: '登录失败，请稍后重试' }
  }
}

/**
 * 用户登出
 */
export async function signOut() {
  if (!isAvailable()) {
    currentUser = null
    return true
  }

  try {
    const db = getDb()
    await db.auth.signOut()
    currentUser = null
    console.log('[Auth] 已登出')
    return true
  } catch (err) {
    console.error('[Auth] 登出失败:', err)
    return false
  }
}

/**
 * 获取当前用户
 * @returns {object|null}
 */
export function getCurrentUser() {
  return currentUser
}

/**
 * 初始化认证界面
 * 渲染登录/注册表单，处理交互逻辑
 */
export function initAuthUI() {
  const authContainer = document.getElementById('auth-container')
  const appContainer = document.getElementById('app-container')
  const errorDiv = document.getElementById('auth-error')

  if (!authContainer) return

  let isLoginMode = true // 默认登录模式

  // 切换登录/注册
  const switchLink = document.getElementById('auth-switch-link')
  const authTitle = document.getElementById('auth-title')
  const authSubtitle = document.getElementById('auth-subtitle')
  const submitBtn = document.getElementById('auth-submit-btn')

  if (switchLink) {
    switchLink.addEventListener('click', () => {
      isLoginMode = !isLoginMode
      if (isLoginMode) {
        authTitle.textContent = '欢迎回来'
        authSubtitle.textContent = '登录你的生活工作台'
        submitBtn.textContent = '登录'
        switchLink.textContent = '还没有账号？去注册'
      } else {
        authTitle.textContent = '创建账号'
        authSubtitle.textContent = '开始记录你的生活与工作'
        submitBtn.textContent = '注册'
        switchLink.textContent = '已有账号？去登录'
      }
      if (errorDiv) errorDiv.classList.remove('show')
    })
  }

  // 提交表单
  const authForm = document.getElementById('auth-form')
  if (authForm) {
    authForm.addEventListener('submit', async (e) => {
      e.preventDefault()

      const email = document.getElementById('auth-email').value.trim()
      const password = document.getElementById('auth-password').value

      if (!email || !password) {
        showError('请填写邮箱和密码')
        return
      }

      submitBtn.disabled = true
      submitBtn.textContent = '处理中...'

      let result
      if (isLoginMode) {
        result = await signIn(email, password)
      } else {
        if (password.length < 6) {
          showError('密码至少需要 6 个字符')
          submitBtn.disabled = false
          submitBtn.textContent = isLoginMode ? '登录' : '注册'
          return
        }
        result = await signUp(email, password)
      }

      submitBtn.disabled = false
      submitBtn.textContent = isLoginMode ? '登录' : '注册'

      if (result.success) {
        // 登录/注册成功，通知 app.js 切换到工作台界面
        document.dispatchEvent(new CustomEvent('auth:login-success'))
      } else {
        showError(result.error)
      }
    })
  }
}

/**
 * 显示错误信息
 */
function showError(message) {
  const errorDiv = document.getElementById('auth-error')
  if (errorDiv) {
    errorDiv.textContent = message
    errorDiv.classList.add('show')
  }
}
